/**
 * Roles Page Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Main page for role management with list and CRUD operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { roleService } from '../../services/admin';
import type { Role, RoleListResponse } from '../../types/admin';
import { RolesList, RoleFormModal } from '../../components/admin/roles';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

const RolesPage: React.FC = () => {
  // State
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirmation dialog
  const [confirmDelete, setConfirmDelete] = useState<Role | null>(null);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: RoleListResponse = await roleService.listRoles();
      setRoles(response.roles);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Handlers
  const handleCreateOrUpdateRole = async (data: {
    name: string;
    description?: string;
    permissions: string[];
    is_default?: boolean;
  }) => {
    setIsSubmitting(true);
    try {
      if (selectedRole && isDuplicate) {
        // Duplicate
        await roleService.duplicateRole(selectedRole.id, {
          new_name: data.name,
          new_description: data.description,
        });
        // If permissions changed, update the new role
        toast.success('Role duplicated successfully');
      } else if (selectedRole) {
        // Update
        await roleService.updateRole(selectedRole.id, data);
        toast.success('Role updated successfully');
      } else {
        // Create
        await roleService.createRole(data);
        toast.success('Role created successfully');
      }

      setShowRoleModal(false);
      setSelectedRole(null);
      setIsDuplicate(false);
      fetchRoles();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save role'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    try {
      await roleService.deleteRole(role.id);
      toast.success('Role deleted successfully');
      setConfirmDelete(null);
      fetchRoles();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete role'));
    }
  };

  const openCreateModal = () => {
    setSelectedRole(null);
    setIsDuplicate(false);
    setShowRoleModal(true);
  };

  const openEditModal = (role: Role) => {
    setSelectedRole(role);
    setIsDuplicate(false);
    setShowRoleModal(true);
  };

  const openDuplicateModal = (role: Role) => {
    setSelectedRole(role);
    setIsDuplicate(true);
    setShowRoleModal(true);
  };

  // Calculate stats
  const systemRolesCount = roles.filter((r) => r.is_system_role).length;
  const customRolesCount = roles.filter((r) => !r.is_system_role).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-gray-500 mt-1">
            Manage user roles and their associated permissions
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus size={18} className="mr-2" />
          Create Role
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Roles</p>
          <p className="text-2xl font-bold text-gray-900">{roles.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">System Roles</p>
          <p className="text-2xl font-bold text-gray-900">{systemRolesCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Custom Roles</p>
          <p className="text-2xl font-bold text-gray-900">{customRolesCount}</p>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>System roles</strong> (Admin, Advisor, Viewer) cannot be modified but can be duplicated to create custom variants.
          Custom roles can be fully edited or deleted.
        </p>
      </div>

      {/* Roles list */}
      <RolesList
        roles={roles}
        isLoading={isLoading}
        onEdit={openEditModal}
        onDuplicate={openDuplicateModal}
        onDelete={(role) => setConfirmDelete(role)}
      />

      {/* Role form modal */}
      <RoleFormModal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedRole(null);
          setIsDuplicate(false);
        }}
        onSubmit={handleCreateOrUpdateRole}
        role={selectedRole}
        isLoading={isSubmitting}
        isDuplicate={isDuplicate}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete Role"
        description={`Are you sure you want to delete the role "${confirmDelete?.name}"? This action cannot be undone.`}
        warning={
          confirmDelete && confirmDelete.user_count > 0
            ? `This role has ${confirmDelete.user_count} user(s) assigned. You must reassign them to a different role before deleting.`
            : undefined
        }
        onConfirm={() => confirmDelete && handleDeleteRole(confirmDelete)}
        confirmText="Delete"
        destructive
        confirmDisabled={!!confirmDelete && confirmDelete.user_count > 0}
      />
    </div>
  );
};

export default RolesPage;
